import {Box, getSize, HStack, Integer, useWindowDimensions} from 'platform/foundation';
import styled from 'styled-components';

import {ReactNode, useState} from 'react';

import {isNil, isNotNil, move} from 'ramda';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

export interface CollapsibleChipItem {
  id: string;
  measureKey?: string;
  node: ReactNode;
}

/** Gets the ids of the items on screen, in the displayed order, and the row to wrap. */
export type RenderVisibleRow = (visibleIds: string[], row: ReactNode) => ReactNode;

interface CollapsibleChipRowsProps extends TestIdProps {
  rowLimit: number;
  spacing: Integer;
  isExpanded: boolean;
  items: CollapsibleChipItem[];
  pinnedId?: string | Nullish;
  /** Rendered right after the last visible item. May render nothing. */
  renderTrailing: (hiddenCount: number) => ReactNode;
  /** Wraps the visible row (not the hidden measuring copy). */
  renderVisibleRow?: RenderVisibleRow;
}

/** Tops closer than this belong to the same wrapped row. */
const ROW_EPSILON = 1;

/**
 * Wrapping row of chips clamped to `rowLimit` rows. Like `FeatureListRow`, a hidden copy of the
 * row is rendered to read back which row each chip lands on, so the fit matches the browser.
 */
export function CollapsibleChipRows(props: CollapsibleChipRowsProps) {
  const measureKey = JSON.stringify([
    props.isExpanded,
    props.items.map((item) => item.measureKey ?? item.id),
  ]);

  return <MeasuredChipRows key={measureKey} {...props} />;
}

function MeasuredChipRows(props: CollapsibleChipRowsProps) {
  const naturalOrder = props.items.map((_, index) => index);
  const {width: windowWidth} = useWindowDimensions();
  const [visibleCount, setVisibleCount] = useState(props.items.length);
  // Fit order only - the pinned item is moved into the last visible slot; expanded view ignores it.
  const [order, setOrder] = useState(naturalOrder);

  const commit = (visible: number) => {
    const pinnedPosition = order.findIndex(
      (itemIndex) => props.items[itemIndex].id === props.pinnedId
    );

    // Every move puts the pinned item strictly earlier, so this settles after a few passes.
    if (visible > 0 && pinnedPosition >= visible) {
      setOrder(move(pinnedPosition, visible - 1, order));
      return;
    }

    setVisibleCount((current) => (current === visible ? current : visible));
  };

  const measure = (container: HTMLDivElement) => {
    const itemEls = Array.from(container.querySelectorAll<HTMLElement>('[data-chip-role="item"]'));
    const completeTrailingEl = container.querySelector<HTMLElement>(
      '[data-chip-role="complete-trailing"]'
    );
    const probeEl = container.querySelector<HTMLElement>(
      '[data-chip-role="clipped-trailing-probe"]'
    );

    if (itemEls.length !== order.length || isNil(completeTrailingEl) || isNil(probeEl)) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const itemRects = itemEls.map((el) => el.getBoundingClientRect());

    // Rows come from the item grid - a new row starts where an item's top jumps below the previous
    // one. Dividing by the element's own height would misplace anything taller than a chip, and
    // the trailing content is exactly that (`Button` 32px next to `Tag` 24px).
    const rowTops = itemRects
      .map((rect) => rect.top)
      .filter((top, index, tops) => index === 0 || top - tops[index - 1] > ROW_EPSILON);

    // Matched on the vertical center, because items of different heights on one row are centered
    // against each other and so do not share a `top`.
    const rowOf = (rect: DOMRect) => {
      const center = rect.top + rect.height / 2;
      const nextRow = rowTops.findIndex((top) => top > center);

      return nextRow === -1 ? Math.max(rowTops.length - 1, 0) : Math.max(nextRow - 1, 0);
    };

    const lastItemRow = itemRects.length > 0 ? rowOf(itemRects[itemRects.length - 1]) : 0;

    // `renderTrailing(0)` may render nothing (zero-height marker) - then the last item decides.
    const completeTrailingRect = completeTrailingEl.getBoundingClientRect();
    const lastRow = completeTrailingRect.height === 0 ? lastItemRow : rowOf(completeTrailingRect);

    if (lastRow < props.rowLimit) {
      commit(order.length);
      return;
    }

    const trailingWidth = probeEl.getBoundingClientRect().width;
    // Read back from the rendered row: `getSize` returns a `calc()` expression, not pixels.
    const gap = parseFloat(getComputedStyle(container).columnGap) || 0;
    let visible = itemRects.filter((rect) => rowOf(rect) < props.rowLimit).length;

    // Drop items until the trailing content fits beside the last one or has the last row to itself.
    while (visible > 0) {
      const lastRect = itemRects[visible - 1];
      const isLastRowFree = rowOf(lastRect) < props.rowLimit - 1;
      const fitsBeside = containerRect.right - lastRect.right - gap >= trailingWidth;

      if (isLastRowFree || fitsBeside) {
        break;
      }

      visible -= 1;
    }

    commit(visible);
  };

  const displayedOrder = props.isExpanded ? naturalOrder : order.slice(0, visibleCount);
  const hiddenCount = order.length - visibleCount;

  const renderVisibleRow: RenderVisibleRow = props.renderVisibleRow ?? ((_visibleIds, row) => row);

  const visibleRow = (
    <HStack
      wrap
      spacing={props.spacing}
      align="center"
      grow={1}
      minWidth={0}
      data-testid={suffixTestId('visibleRow', props)}
    >
      {displayedOrder.map((itemIndex) => (
        <div key={props.items[itemIndex].id}>{props.items[itemIndex].node}</div>
      ))}
      {props.renderTrailing(hiddenCount)}
    </HStack>
  );

  return (
    <Box position="relative" width="100%">
      {renderVisibleRow(
        displayedOrder.map((itemIndex) => props.items[itemIndex].id),
        visibleRow
      )}

      {/* Hidden copy of the row for measuring - remounted on resize and after a pinned reorder. */}
      <MeasuringRow
        key={`calculator-${windowWidth}-${order.join(',')}`}
        ref={(container) => {
          if (isNotNil(container)) {
            measure(container);
          }
        }}
        $spacing={props.spacing}
        aria-hidden
      >
        {order.map((itemIndex) => (
          <div key={props.items[itemIndex].id} data-chip-role="item">
            {props.items[itemIndex].node}
          </div>
        ))}
        <div data-chip-role="complete-trailing">{props.renderTrailing(0)}</div>
        {/* Probed with the highest possible count - "+12 more" is wider than "+9 more". */}
        <Box position="absolute">
          <div data-chip-role="clipped-trailing-probe">
            {props.renderTrailing(props.items.length)}
          </div>
        </Box>
      </MeasuringRow>
    </Box>
  );
}

// The only styled element left: `visibility: hidden` keeps the duplicated chips out of the tab
// order and the accessibility tree, and `Box` has no `visibility` prop.
const MeasuringRow = styled.div<{$spacing: Integer}>`
  position: absolute;
  inset: 0 0 auto 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({$spacing}) => getSize($spacing)};
  visibility: hidden;
  pointer-events: none;
  height: 0;
  overflow: hidden;
`;
